import { Product } from '@/types'
import type { PaymentInterval } from '@/types/purchase/PaymentInterval'
import { formatCurrency } from '@/utils'
import { getPaymentCount } from './getPaymentCount'

// Use decimal (SI) units to match database storage (1 GB = 10^9 bytes)
const GB = 1_000_000_000

// Hosting plan promoted as "most popular" on the webhosting configurator
export const POPULAR_HOSTING_SLUG_SUFFIX = 'mega'

const PERIOD_KEYS: Record<number, string> = {
  1: 'common.termPrice.period.month',
  3: 'common.termPrice.period.quarter',
  6: 'common.termPrice.period.halfYear',
  12: 'common.termPrice.period.year'
}

export type TermPrice = {
  /** Amount charged per billing period. */
  billed: string
  /** Translated period the billed amount covers ("Jahr"). */
  period: string
  /** Monthly equivalent, set only for prepaid terms longer than a month. */
  monthly?: string
}

/**
 * PAngV: for a prepaid term the amount charged per period is the main price and
 * the monthly equivalent is secondary. Prices are stored per month and a term
 * costs that times its months, without discount. `billedAmount` overrides that
 * product for a net figure, which is rounded per cycle (12 x 1,67 is not 20,07).
 */
/** Translated period one term of interval covers ("Jahr"). */
export const termPeriod = (interval: PaymentInterval | undefined, t: (key: string) => string): string =>
  t(PERIOD_KEYS[getPaymentCount(interval)]!)

export const formatTermPrice = (
  monthlyAmount: number,
  currency: string,
  interval: PaymentInterval | undefined,
  t: (key: string) => string,
  bcp47Locale: string,
  billedAmount?: number
): TermPrice => {
  const months = getPaymentCount(interval)
  return {
    billed: formatCurrency(billedAmount ?? monthlyAmount * months, currency, bcp47Locale),
    period: termPeriod(interval, t),
    monthly: months > 1 ? formatCurrency(monthlyAmount, currency, bcp47Locale) : undefined
  }
}

const UNIT_PRICE_LABEL_KEYS: Partial<Record<PaymentInterval, string>> = {
  quarterly: 'cart.item.pricePerQuarter',
  'half-annually': 'cart.item.pricePerHalfYear',
  annually: 'cart.item.pricePerYear'
}

/** Cart label for the amount billed per term; monthly when there is no prepaid term. */
export const unitPriceLabelKey = (interval: PaymentInterval | undefined): string =>
  (interval && UNIT_PRICE_LABEL_KEYS[interval]) || 'cart.item.pricePerMonth'

/** formatTermPrice for a product's default price; null when it has none. */
export const formatProductTermPrice = (
  product: Product,
  interval: PaymentInterval | undefined,
  t: (key: string) => string,
  bcp47Locale: string
): TermPrice | null => {
  const def = product.price?.find((x) => x.default && x.amount && x.currency)
  if (!def?.amount || !def.currency) return null
  return formatTermPrice(def.amount / 100, def.currency, interval, t, bcp47Locale)
}

/**
 * Clone a product to avoid mutation
 */
export const cloneProduct = (product: Product): Product => ({
  ...product,
  features: product.features ? { ...product.features } : undefined,
  price: product.price ? product.price.map((price) => ({ ...price })) : undefined,
  payment_interval: product.payment_interval ? [...product.payment_interval] : undefined
})

/**
 * Get product storage size in GB
 */
export const getProductSizeInGb = (product: Product): number => {
  const size = Number((product.features as Record<string, unknown>)?.size ?? 0)
  if (!size || Number.isNaN(size)) return 0
  return Math.round(size / GB)
}

/**
 * Sort products by price (ascending)
 */
export const sortProductsByPrice = (a: Product, b: Product): number => {
  const priceA = a.price?.find((price) => price.active && price.amount)?.amount ?? Number.MAX_VALUE
  const priceB = b.price?.find((price) => price.active && price.amount)?.amount ?? Number.MAX_VALUE
  return priceA - priceB
}

/**
 * Get the default monthly price amount for a product
 */
export const getDefaultMonthlyAmount = (product: Product): number | undefined => {
  const price = product.price?.find((x) => x.default && x.amount)
  return price?.amount
}

export type ProductGroup = {
  key: string
  title: string
  description: string
  items: Product[]
}

/**
 * Group products by tier based on storage size
 */
export const groupProductsByTier = (products: Product[]): ProductGroup[] => {
  if (!products.length) return []
  const sorted = [...products].sort(sortProductsByPrice)

  const groups: Array<{ key: string; title: string; description: string; match: (p: Product) => boolean }> = [
    {
      key: 'starter',
      title: 'Starter Pakete',
      description: 'Ideal für Blogs, Portfolios und kleine Projekte mit moderatem Speicherbedarf.',
      match: (product) => getProductSizeInGb(product) <= 120
    },
    {
      key: 'growth',
      title: 'Growth Pakete',
      description: 'Für Shops, Communities und Applikationen mit wachsenden Anforderungen.',
      match: (product) => {
        const size = getProductSizeInGb(product)
        return size > 120 && size <= 520
      }
    },
    {
      key: 'enterprise',
      title: 'Enterprise Pakete',
      description: 'Maximale Performance, höchste Kapazitäten und Premium-Support für anspruchsvolle Workloads.',
      match: (product) => getProductSizeInGb(product) > 520
    }
  ]

  return groups
    .map((group) => ({
      key: group.key,
      title: group.title,
      description: group.description,
      items: sorted.filter(group.match)
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * Build storage tiers dynamically from products
 */
export type StorageTier = {
  index: number
  label: string
  bytes: number
  slug: string
}

export function buildStorageTiers(products: Product[]): StorageTier[] {
  // Filter hosting products with size, sort by size ascending
  const hostingWithSize = products
    .filter((p) => p.active && p.public && (p.features as Record<string, unknown>)?.type === 'hosting')
    .filter((p) => typeof (p.features as Record<string, unknown>)?.size === 'number')
    .sort((a, b) => {
      const sizeA = (a.features as Record<string, unknown>).size as number
      const sizeB = (b.features as Record<string, unknown>).size as number
      return sizeA - sizeB
    })

  if (hostingWithSize.length === 0) {
    // Fallback to static tiers if no products
    return defaultStorageTiers
  }

  const tiers: StorageTier[] = hostingWithSize.map((p, index) => {
    const bytes = (p.features as Record<string, unknown>).size as number
    return {
      index,
      label: formatStorageSize(bytes),
      bytes,
      slug: p.slug || ''
    }
  })

  // Add enterprise tier at the end (last tier size +)
  const lastTier = tiers[tiers.length - 1]
  tiers.push({
    index: tiers.length,
    label: `${lastTier?.label || '2 TB'}+`,
    bytes: Infinity,
    slug: 'enterprise'
  })

  return tiers
}

/**
 * Format bytes to human-readable storage size
 */
export function formatStorageSize(bytes: number): string {
  const TB = 1_000_000_000_000
  if (bytes >= TB) {
    const tb = bytes / TB
    return `${tb % 1 === 0 ? tb : tb.toFixed(1)} TB`
  }
  const gb = bytes / GB
  return `${gb % 1 === 0 ? gb : gb.toFixed(0)} GB`
}

/**
 * Default storage tiers (fallback when no products available)
 */
export const defaultStorageTiers: StorageTier[] = [
  { index: 0, label: '50 GB', bytes: 50 * GB, slug: 'hosting-nano' },
  { index: 1, label: '100 GB', bytes: 100 * GB, slug: 'hosting-micro' },
  { index: 2, label: '200 GB', bytes: 200 * GB, slug: 'hosting-mega' },
  { index: 3, label: '500 GB', bytes: 500 * GB, slug: 'hosting-giga' },
  { index: 4, label: '1 TB', bytes: 1000 * GB, slug: 'hosting-tera' },
  { index: 5, label: '2 TB', bytes: 2000 * GB, slug: 'hosting-peta' },
  { index: 6, label: '2 TB+', bytes: Infinity, slug: 'enterprise' }
]

// Legacy export for backwards compatibility
export const storageTiers = defaultStorageTiers

export const storageTierLabels: Record<number, string> = {
  0: '50 GB',
  1: '100 GB',
  2: '200 GB',
  3: '500 GB',
  4: '1 TB',
  5: '2 TB',
  6: '2 TB+'
}
